import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "react-feather";
import "./searchable-card.scss";

export interface SearchableCardOption {
  label: string;
  value: string;
}

export type SearchableCardSize = "small" | "medium" | "large";

export interface SearchableCardProps {
  /** Label displayed above the trigger */
  label?: string;
  /** Array of selectable options */
  options: SearchableCardOption[];
  /** Controlled selected value */
  value?: string;
  /** Initial value for uncontrolled usage */
  defaultValue?: string;
  /** Trigger placeholder when nothing is selected */
  placeholder?: string;
  /** Called when the selected option changes */
  onChange?: (value: string, option: SearchableCardOption) => void;
  /** Placeholder inside the search input of the popover */
  searchPlaceholder?: string;
  /** Heading displayed between divider and options list */
  heading?: string;
  /** Called when the search input value changes */
  onSearchChange?: (searchTerm: string) => void;
  /** Called when the popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** Called when the user clicks the "Create" row (shown when the search term has no exact label match). Receives the search term. */
  onCreateOption?: (value: string) => void;
  /** Called when any option is clicked, receives the clicked option */
  onOptionClick?: (option: SearchableCardOption) => void;
  /** When true, shows a loading state in the options list instead of options. Default false. */
  loading?: boolean;
  /** Message shown in the options area while `loading` is true. Default "Loading...". */
  loadingMessage?: string;
  disabled?: boolean;
  required?: boolean;
  /** Error message shown below trigger; also applies a red border */
  error?: string;
  /** Hint text shown below trigger (hidden when error is present) */
  hint?: string;
  /** Size variant: small (32px), medium (40px), large (48px) */
  size?: SearchableCardSize;
  /**
   * When true, the trigger stays mounted while the popover is open so both
   * are visible simultaneously (overlay mode). When false (default) the
   * trigger is unmounted while the popover is open (replace mode).
   */
  keepTriggerMounted?: boolean;
  /**
   * A ref to a DOM element that the popover portal should render into.
   * Useful when the component lives inside a modal or drawer and you want
   * the popover to stay within that stacking context. When omitted the
   * popover portals into document.body.
   */
  portalTarget?: React.RefObject<HTMLElement>;
  /** @deprecated The popover is now always rendered via a portal. This prop is a no-op and will be removed in a future release. */
  shouldUsePortal?: boolean;
  className?: string;
  id?: string;
}

export const SearchableCard: React.FC<SearchableCardProps> = ({
  label,
  options,
  value: controlledValue,
  defaultValue,
  placeholder = "Select...",
  onChange,
  searchPlaceholder = "Search for an option...",
  heading = "Select an option or create one",
  onSearchChange,
  onOpenChange,
  onCreateOption,
  onOptionClick,
  loading = false,
  loadingMessage = "Loading...",
  disabled = false,
  required = false,
  error,
  hint,
  size = "small",
  keepTriggerMounted = false,
  portalTarget,
  className = "",
  id,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });
  const [createdOptions, setCreatedOptions] = useState<SearchableCardOption[]>(
    []
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const setOpenState = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  const activeValue = isControlled ? controlledValue : internalValue;
  const mergedOptions = useMemo(() => {
    if (createdOptions.length === 0) return options;
    const existingValues = new Set(options.map((o) => o.value));
    const extras = createdOptions.filter((o) => !existingValues.has(o.value));
    return extras.length > 0 ? [...options, ...extras] : options;
  }, [options, createdOptions]);
  const selectedOption = mergedOptions.find((o) => o.value === activeValue);
  const hasActiveValue = activeValue !== undefined && activeValue !== "";
  const displayLabel =
    selectedOption?.label ?? (hasActiveValue ? activeValue : placeholder);
  const hasSelection = !!selectedOption || hasActiveValue;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return mergedOptions;
    const lower = searchTerm.toLowerCase();
    return mergedOptions.filter((o) => o.label.toLowerCase().includes(lower));
  }, [mergedOptions, searchTerm]);

  const updatePortalPosition = useCallback(() => {
    if (!containerRef.current) return;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const gap = 4;
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0;

    if (portalTarget?.current) {
      // Render inside the target container with position:absolute so coords
      // are relative to the container regardless of CSS transforms on it.
      const containerRect = portalTarget.current.getBoundingClientRect();
      const scrollTop = portalTarget.current.scrollTop;
      const scrollLeft = portalTarget.current.scrollLeft;

      const spaceBelow =
        containerRect.bottom - triggerRect.bottom - gap;
      const spaceAbove =
        triggerRect.top - containerRect.top - gap;

      const placeAbove =
        popoverHeight > spaceBelow && spaceAbove > spaceBelow;

      const top = placeAbove
        ? triggerRect.top - containerRect.top + scrollTop - popoverHeight - gap
        : triggerRect.bottom - containerRect.top + scrollTop + gap;
      const left = triggerRect.left - containerRect.left + scrollLeft;

      setPortalStyle({
        position: "absolute",
        top,
        left,
        minWidth: triggerRect.width,
        zIndex: 9999,
        visibility: "visible",
      });
    } else {
      // Default: portal to document.body, position:fixed against the viewport.
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom - gap;
      const spaceAbove = triggerRect.top - gap;

      const placeAbove =
        popoverHeight > spaceBelow && spaceAbove > spaceBelow;
      const top = placeAbove
        ? triggerRect.top - popoverHeight - gap
        : triggerRect.bottom + gap;

      setPortalStyle({
        position: "fixed",
        top,
        left: triggerRect.left,
        minWidth: triggerRect.width,
        zIndex: 9999,
        visibility: "visible",
      });
    }
  }, [portalTarget]);

  // Two-pass positioning: the portal renders invisible first, then
  // useLayoutEffect measures its height and commits the final position.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPortalStyle({ visibility: "hidden" });
      return;
    }
    updatePortalPosition();
  }, [isOpen, updatePortalPosition, loading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target) ?? false;
      const insidePopover = popoverRef.current?.contains(target) ?? false;

      if (!insideContainer && !insidePopover) {
        setOpenState(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setOpenState]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", updatePortalPosition, true);
    window.addEventListener("resize", updatePortalPosition);
    return () => {
      window.removeEventListener("scroll", updatePortalPosition, true);
      window.removeEventListener("resize", updatePortalPosition);
    };
  }, [isOpen, updatePortalPosition]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (prev) setSearchTerm("");
      onOpenChange?.(next);
      return next;
    });
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpenState(false);
      setSearchTerm("");
      return;
    }
    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Enter"
    ) {
      event.preventDefault();
      if (!isOpen) {
        setOpenState(true);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    onSearchChange?.(val);
  };

  const trimmedSearch = searchTerm.trim();
  const hasExactLabelMatch =
    trimmedSearch.length > 0 &&
    mergedOptions.some(
      (o) => o.label.trim().toLowerCase() === trimmedSearch.toLowerCase()
    );
  const showCreateRow =
    !loading && trimmedSearch.length > 0 && !hasExactLabelMatch;

  const handleCreateClick = () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return;

    const newOption: SearchableCardOption = { label: trimmed, value: trimmed };
    setCreatedOptions((prev) =>
      prev.some((o) => o.value === newOption.value) ? prev : [...prev, newOption]
    );
    if (!isControlled) {
      setInternalValue(newOption.value);
    }
    onCreateOption?.(trimmed);
    onChange?.(newOption.value, newOption);
    onOptionClick?.(newOption);
    setOpenState(false);
    setSearchTerm("");
    triggerRef.current?.focus();
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCreateClick();
    }
    if (e.key === "Escape") {
      setOpenState(false);
      setSearchTerm("");
      triggerRef.current?.focus();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpenState(false);
      setSearchTerm("");
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const firstItem = menuRef.current?.querySelector<HTMLDivElement>(
        ".searchable-card__option, .searchable-card__create-option"
      );
      firstItem?.focus();
    }
  };

  const handleSelect = (option: SearchableCardOption) => {
    if (!isControlled) {
      setInternalValue(option.value);
    }
    onChange?.(option.value, option);
    onOptionClick?.(option);
    setOpenState(false);
    setSearchTerm("");
    triggerRef.current?.focus();
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent,
    option: SearchableCardOption,
    index: number
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(option);
      return;
    }
    if (e.key === "Escape") {
      setOpenState(false);
      setSearchTerm("");
      triggerRef.current?.focus();
      return;
    }

    const items = menuRef.current?.querySelectorAll<HTMLDivElement>(
      ".searchable-card__option"
    );
    if (!items) return;

    let nextIndex = -1;
    if (e.key === "ArrowDown") {
      nextIndex = (index + 1) % items.length;
    } else if (e.key === "ArrowUp") {
      if (index === 0) {
        searchInputRef.current?.focus();
        e.preventDefault();
        return;
      }
      nextIndex = (index - 1 + items.length) % items.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = items.length - 1;
    }

    if (nextIndex >= 0) {
      e.preventDefault();
      items[nextIndex]?.focus();
    }
  };

  const renderPopover = () => (
    <div
      ref={popoverRef}
      className="searchable-card__popover"
      style={portalStyle}
      role="dialog"
      aria-label={heading}
    >
      <div className="searchable-card__search">
        <div className="searchable-card__search-field">
          <input
            ref={searchInputRef}
            type="text"
            className="searchable-card__search-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            aria-label={searchPlaceholder}
          />
          <span className="searchable-card__search-icon" aria-hidden="true">
            <Search size={14} />
          </span>
        </div>
      </div>

      <div className="searchable-card__divider" />

      <p className="searchable-card__heading">{heading}</p>

      <div
        className="searchable-card__menu"
        ref={menuRef}
        role="listbox"
        aria-label={label}
        aria-busy={loading}
      >
        <div className="searchable-card__menu-items">
          {loading ? (
            <div
              className="searchable-card__loading"
              role="status"
              aria-live="polite"
            >
              {loadingMessage}
            </div>
          ) : (
            <>
              {filteredOptions.length === 0 && !showCreateRow && (
                <div className="searchable-card__empty">No options found</div>
              )}
              {showCreateRow && (
                <div
                  className="searchable-card__create-option"
                  role="option"
                  aria-selected={false}
                  tabIndex={0}
                  onClick={handleCreateClick}
                  onKeyDown={handleCreateKeyDown}
                >
                  <button
                    type="button"
                    className="searchable-card__create-label"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateClick();
                    }}
                    tabIndex={-1}
                  >
                    Create
                  </button>
                  <div className="searchable-card__create-content">
                    <span className="searchable-card__create-value">
                      {searchTerm.trim()}
                    </span>
                  </div>
                </div>
              )}
              {filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`searchable-card__option ${option.value === activeValue ? "searchable-card__option--selected" : ""}`}
                  role="option"
                  aria-selected={option.value === activeValue}
                  tabIndex={0}
                  onClick={() => handleSelect(option)}
                  onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
                >
                  <div className="searchable-card__option-content">
                    <span className="searchable-card__option-label">
                      {option.label}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const showTrigger = !isOpen || keepTriggerMounted;

  return (
    <div
      className={`searchable-card ${error ? "searchable-card--error" : ""} ${className}`}
      ref={containerRef}
      id={id}
    >
      {label && (
        <label className="searchable-card__label">
          {label}
          {required && <span className="searchable-card__required"> *</span>}
        </label>
      )}

      {showTrigger && (
        <>
          <div className="searchable-card__wrapper">
            <button
              ref={triggerRef}
              className={`searchable-card__trigger searchable-card__trigger--${size} ${!hasSelection ? "searchable-card__trigger--placeholder" : ""} ${error ? "searchable-card__trigger--error" : ""}`}
              type="button"
              onClick={handleToggle}
              onKeyDown={handleTriggerKeyDown}
              disabled={disabled}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              aria-label={label}
              aria-invalid={!!error}
            >
              <span className="searchable-card__value">{displayLabel}</span>
              <span className="searchable-card__icon" aria-hidden="true">
                <ChevronDown size={16} />
              </span>
            </button>
          </div>

          {hint && !error && (
            <div className="searchable-card__hint">{hint}</div>
          )}
          {error && <div className="searchable-card__error-text">{error}</div>}
        </>
      )}

      {isOpen &&
        createPortal(
          renderPopover(),
          portalTarget?.current ?? document.body
        )}
    </div>
  );
};
